<template>
    <div class="course-comments-content">
        <div class="default-table"></div>
        <!-- <portal to="modals" v-if="displayBulkDeleteModal">
            <DeleteModal
                @delete="bulkDelete()"
                @cancel="displayBulkDeleteModal = false"
                :title="t('course.delete_all')"
                :centred="true"
                :loading="bulkLoader"
            />
        </portal>-->

        <default-title class="mb-20" v-if="displayTitle && langsKeys" large>
            {{ t("course.course_card.comments") }}
            <span v-if="comments.length" class="text-blue">
                {{ comments.filter(comment => !comment.is_restricted).length }}
            </span>
        </default-title>

        <slot name="custom-title"></slot>

        <CommentsActions
            class="mb-10"
            v-if="parent === 'expert'"
            @next="goNextUnseen()"
            :disabled="!!loadingGetComment"
            :allowBulkActions="allowBulkActions"
            :unseen-comments="unreadIds"
            :all-read="!unreadIds.length"
            :all-hidden="comments && comments.every(comment => comment.is_restricted)"
            :commentable-type="commentable_type"
            :course-id="courseId"
            :lesson-id="lessonId"
            :attempt-id="rootAttemptId"
            :isMarathon="isMarathon"
            @bulk:read="bulkRead()"
            @bulk:delete="prepareBulkDelete()"
            @bulk:restrict="bulkRestrict()"
        />
        <CommentsPublicCreate
            ref="create"
            v-if="(!commentable || !commentable.deleted_at) && isUserCommentAllowed && !disableAnswers"
            :is-public="isPublic"
            :question-id="questionId"
            :is-private-selection="isPrivateSelection"
            :advanced-panel="advancedPanel"
            :unit-id="unitId"
            :course-id="courseId"
            :commentable_type="commentable_type"
            :quiz-id="quizId"
            :langsKeys="langsKeys"
            :allow-answer="allowAnswer"
            :lessonId="lessonId"
            :attempt-id="rootAttemptId"
            :answered-comment-type="answeredCommentType"
            :user="user"
            :parent="parent"
            :allowCreateComment="allowCreateComment"
            :commentParentId="toAnswerCommentId"
            :viewed-user="viewedUser"
            :allow-upload-file="allowUploadFile"
            :allow-audio-response="allowAudioResponse"
            :allow-video-response="allowVideoResponse"
            :allow-public-answer="computedAllowPublicAnswer"
            :allow-text-answer="allowTextAnswer"
            :curators="curators"
            @push:comment="pushComment($event)"
            @update:commentable="$emit('update:commentable', $event)"
        />

        <transition name="fade" mode="out-in">
            <SpinLoader key="loader" for-page v-if="loadingGetComment" />

            <EmptyContent
                key="empty-content"
                icon-name="FiltersEmptyInboxIcon"
                :title="t('course.no_comments')"
                v-else-if="!comments.length && parent === 'expert'"
            />

            <transition-group key="group" v-else-if="!forseRefresh && comments.length" tag="div" :name="transitionName">
                <CommentsPublicComment
                    :class="{ disabled: loadingGetComment }"
                    v-for="(comment, comment_index) in displayedComments"
                    @update:comment="updateComment(comment_index, $event)"
                    @delete:comment="deleteComment(comment_index)"
                    @read:comment="readComment($event)"
                    @unread:comment="unreadComment($event)"
                    @update:commentable="$emit('update:commentable', $event)"
                    :commentable="commentable"
                    :selection-comment-id="selectionCommentId"
                    :commentable_type="commentable_type"
                    :quiz-id="quizId"
                    :is-public="isPublic"
                    :comment="comment"
                    :advanced-panel="advancedPanel"
                    :langsKeys="langsKeys"
                    :hide-actions="hideActions"
                    :lessonId="lessonId"
                    :attempt-id="rootAttemptId"
                    :commentParentId="toAnswerCommentId"
                    :viewed-user="viewedUser"
                    :user="user"
                    :level="0"
                    :unit-id="unitId"
                    :allow-public-answer="allowPublicAnswer"
                    :question-id="questionId"
                    :allowCreateComment="allowCreateComment"
                    :linked="linked"
                    :answeredCommentType="answeredCommentType"
                    :allowUploadFile="allowUploadFile"
                    :allow-video-response="allowVideoResponse"
                    :allowAudioResponse="allowAudioResponse"
                    :allow-answer="allowAnswer"
                    :allow-text-answer="allowTextAnswer"
                    :disable-answers="disableAnswers"
                    :parent="parent"
                    :curators="curators"
                    :curatorPublicFace="curatorPublicFace"
                    :key="'comment-id-' + comment.id"
                />
            </transition-group>
        </transition>
        <transition name="fade">
            <CustomPaginator
                style="transition-delay: 0.5s"
                v-if="meta && meta.total && meta.last_page > 1 && !loadingGetComment"
                key="paginator"
                class="mt-20 ml-auto mb-20"
                :total-pages="meta.last_page"
                :per-page="meta.per_page"
                :total-items="meta.total"
                :current-page="meta.current_page"
                :per-page-cases="[15, 50, 100]"
                light-style
                linked
                blue-style
                prevent-default
                @update:page="onPageChange"
                @update:perPage="onPageChange($event, 'per_page')"
            />
        </transition>
    </div>
</template>

<script>
import axios from "~axios"
import KeyTranslator from "@mixins/keyTranslator"
import { EventBus, EventsList } from "~events"
import { ExternalLoader } from "@helpers"
import DefaultTitle from "@components/Typography/DefaultTitle.vue"
import CustomPaginator from "@components/CustomPaginator.vue"
import SpinLoader from "@components/Loaders/SpinLoader.vue"
import EmptyContent from "../../../modules/Expert/views/Marketing/Mailing/EmptyContent.vue"
import { getPersonalSettings, updatePersonalSettings } from "@common-store/personal-settings"

export default {
    name: "CommentsPublic",
    components: {
        EmptyContent,
        CustomPaginator,
        SpinLoader,
        DefaultTitle,
        CommentsActions: () => import("@components/Comments/CommentsActions.vue"),
        CommentsPublicComment: () => import("@components/Comments/CommentsPublicComment"),
        CommentsPublicCreate: () => import("@components/Comments/CommentsPublicCreate")
    },
    mixins: [KeyTranslator],
    props: {
        filters: {
            type: Object,
            default: null
        },
        advancedPanel: {
            type: Boolean,
            default: false
        },
        allowBulkActions: {
            type: Boolean,
            default: false
        },
        isPublic: {
            type: Boolean,
            default: false
        },
        isPrivateSelection: {
            type: Boolean,
            default: false
        },
        commentsEnable: {
            type: Boolean,
            required: true
        },
        allowCreateComment: {
            type: Boolean,
            default: true
        },
        linked: {
            type: Boolean,
            required: false,
            default: false
        },
        auth: {
            type: Boolean,
            required: true
        },
        noShadow: {
            type: Boolean,
            required: false
        },
        preloadedData: {
            type: [Array, Object],
            required: false,
            default: null
        },
        allowAnswer: {
            type: Boolean,
            required: false,
            default: true
        },
        stickyBottom: {
            type: Boolean,
            required: false,
            default: false
        },
        isOther: {
            type: Boolean,
            required: false,
            default: false
        },
        isMarathon: {
            type: Boolean,
            default: false
        },
        withEmpty: {
            type: Boolean,
            required: false,
            default: false
        },
        hideActions: {
            type: Boolean,
            required: false,
            default: false
        },
        lessonId: {
            type: [Number, String],
            required: false
        },
        unitId: {
            type: [Number, String],
            required: false
        },
        rootAttemptId: {
            type: [Number, String],
            required: false
        },
        questionId: {
            type: [Number, String],
            required: false
        },
        courseId: {
            type: [Number, String],
            required: true
        },
        quizId: {
            type: [Number, String],
            required: false
        },
        toAnswerCommentId: {
            type: [Number, String],
            required: false
        },
        answeredCommentType: {
            type: Boolean,
            default: false
        },
        allowUploadFile: {
            type: Boolean,
            default: false
        },
        allowAudioResponse: {
            type: Boolean,
            default: false
        },
        activeCommentId: {
            type: Number,
            default: 0
        },
        allowVideoResponse: {
            type: Boolean,
            default: false
        },
        displayTitle: {
            type: Boolean,
            default: true
        },
        user: {
            type: Object,
            required: false
        },
        langsProp: {
            type: Object,
            required: false
        },
        viewedUser: {
            type: Object,
            required: false
        },
        disableAnswers: {
            type: Boolean,
            default: false
        },
        /**
         * @values "lesson", "quiz-attempt", "info-units"
         **/
        commentable_type: {
            type: String,
            default: "lesson"
        },
        allowPublicAnswer: {
            type: Boolean,
            default: true
        },
        allowTextAnswer: {
            type: Boolean,
            default: true
        },

        /**
         * @values "column", "column-reverse"
         **/
        direction: {
            type: String,
            default: "column"
        },
        /**
         * @values "expert", "student"
         **/
        parent: {
            type: String,
            default: "lesson"
        }
    },
    data() {
        return {
            loadingGetComment: false,
            nextPageLink: null,
            prevPageLink: null,
            langsKeys: null,
            loadingMore: false,
            comments: [],
            commentable: null,
            meta: null,
            curators: [],
            curatorPublicFace: null,
            curatorsUpdateCount: 0,
            viewedComment: null,
            forseRefresh: false,
            unreadIds: [],
            displayBulkDeleteModal: false,
            bulkLoader: false,
            isUserCommentAllowed: true,
            shouldIgnoreCommentId: false,
            selectionCommentId: null
        }
    },
    async created() {
        document.addEventListener("selectionchange", () => {
            const select = window.getSelection()

            if (
                select &&
                select.type === "Range" &&
                select.anchorNode &&
                select.anchorNode.parentNode &&
                select.anchorNode.parentNode.closest(".course-comment-content")
            ) {
                this.selectionCommentId = parseInt(select.anchorNode.parentNode.closest(".course-comment-content").id)
            } else {
                this.selectionCommentId = null
            }
        })

        if (this.langsProp || (this.$store && this.langs)) {
            this.langsKeys = this.langsProp || this.langs
        } else {
            this.getLangs()
        }
        if (this.auth) {
            if (window.id_hash) this.user.id_hash = window.id_hash
            if (this.commentsEnable) {
                this.loadingGetComment = true
                await this.getComments()

                await this.getCurators()
            }
        }
        EventBus.$on(EventsList.BLURED_CONTENT_CLICK, () => {
            this.displayBulkDeleteModal = false
            EventBus.$emit(EventsList.BLUR_FULL_CONTENT, false)
        })
    },
    async mounted() {
        await ExternalLoader.STYLE(["/build/css/quill.core.css", "/build/css/quill.snow.css"])
    },
    methods: {
        onPageChange(value, key = "current_page") {
            this.meta[key] = value

            if (key === "per_page") {
                updatePersonalSettings("comments_per_page", value)
            }

            this.shouldIgnoreCommentId = true

            setTimeout(() => {
                this.getComments()
            }, 300)
        },
        bulkRead() {
            axios.patch("/comments/bulk-read", this.bulkQuery()).then(() => {
                this.getComments()
                EventBus.$emit("update:unseen")
            })
        },
        prepareBulkDelete() {
            this.displayBulkDeleteModal = true
        },
        bulkDelete() {
            this.bulkLoader = true
            axios
                .delete("/comments/bulk-delete", {
                    data: this.bulkQuery()
                })
                .then(() => {
                    this.getComments()
                    EventBus.$emit("update:unseen")
                })
                .finally(() => {
                    this.bulkLoader = false
                    this.displayBulkDeleteModal = false
                })
        },
        bulkRestrict() {
            axios.patch("/comments/bulk-restrict", this.bulkQuery()).then(() => {
                this.getComments()
                EventBus.$emit("update:unseen")
            })
        },
        bulkQuery() {
            const query = {
                commentable_type: this.commentable_type
            }
            if (this.commentable_type === "quiz-attempt") {
                query.quiz_question_id = this.questionId
                query.commentable_id = this.rootAttemptId
            } else {
                query.commentable_id = this.unitId
            }

            return query
        },
        scrollBottom() {
            this.$nextTick(() => {
                if (this.unreadIds.length) {
                    this.goNextUnseen()
                } else {
                    window.scrollTo({
                        top: document.body.scrollHeight,
                        behavior: "smooth"
                    })
                }
            })
        },
        goNextUnseen() {
            if (!this.viewedComment) {
                this.viewedComment = this.unreadIds[0]
            } else if (this.unreadIds[this.unreadIds.indexOf(this.viewedComment) + 1]) {
                this.viewedComment = this.unreadIds[this.unreadIds.indexOf(this.viewedComment) + 1]
            } else {
                this.viewedComment = this.unreadIds[0]
            }
            this.scrollIntoViewed()
        },
        scrollIntoViewed() {
            const element = document.getElementById(this.viewedComment)
            if (element) {
                const intersectionObserver = new IntersectionObserver(entries => {
                    let [entry] = entries
                    if (entry.isIntersecting) {
                        setTimeout(() => {
                            document.querySelectorAll(".visited").forEach(visited => {
                                visited.classList.remove("visited")
                            })
                            entry.target.classList.add("visited")
                            setTimeout(() => {
                                entry.target.classList.remove("visited")
                            }, 500)
                            intersectionObserver.disconnect()
                        }, 200)
                    }
                })
                intersectionObserver.observe(element)
                element.scrollIntoView({ behavior: "smooth", block: "center" })
            }
        },
        deleteComment(index) {
            this.displayedComments.splice(index, 1)
        },
        readComment(id) {
            if (this.unreadIds.includes(id)) {
                this.unreadIds.splice(this.unreadIds.indexOf(id), 1)
            }
        },
        unreadComment(id) {
            if (!this.unreadIds.includes(id)) {
                this.unreadIds.push(id)
            }
        },
        reversed(comments) {
            return [...comments].reverse()
        },
        pushComment(comment) {
            if (this.direction === "column") {
                this.displayedComments.unshift(comment)
            } else {
                this.displayedComments.push(comment)
            }
            if (this.unreadIds.length === 1) {
                EventBus.$emit("forse-read:comment", this.unreadIds[0])
            }
        },
        updateComments(newCommentId) {
            this.getComments(newCommentId, true)
            if (this.commentable_type === "quiz-attempt") {
                this.$emit("update:comments", newCommentId)
            }
        },
        updateComment(index, comment) {
            this.displayedComments[index] = comment
            this.forseRefresh = true
            this.$nextTick(() => {
                this.forseRefresh = false
            })
        },
        async getComments(newCommentId, preventLoading = false) {
            if (this.commentable_type === "test") {
                this.loadingGetComment = false
                return
            }

            if (!preventLoading) {
                this.loadingGetComment = true
            }

            try {
                let params = {
                    page: this.meta?.current_page || 1,
                    per_page: this.meta?.per_page || getPersonalSettings("comments_per_page") || 50
                }

                if (this.filters) {
                    params = {
                        ...params,
                        ...this.filters
                    }
                }

                let url = `comments/lesson/${this.lessonId}`

                let searchParams = new URLSearchParams(document.location.search)
                let commentUrlId = searchParams.get("comment_id")
                const shouldUseCommentId = !this.shouldIgnoreCommentId

                if (shouldUseCommentId && this.activeCommentId) {
                    params["comment_id"] = this.activeCommentId
                }

                if (shouldUseCommentId && commentUrlId) {
                    params["comment_id"] = commentUrlId
                }

                let isAllComments = false

                if (this.commentable_type === "quiz-attempt") {
                    if (!this.rootAttemptId && this.isPublic) {
                        isAllComments = true
                        url = `lessons/${this.lessonId}/questions/${this.questionId}/comments`
                    } else {
                        const userId = this.viewedUser ? this.viewedUser.id : this.user.id
                        if (this.isOther) {
                            url = `comments/quiz-attempt/${this.rootAttemptId}?exclude_user_id=${userId}`
                        } else {
                            if (!this.rootAttemptId && !newCommentId) {
                                return
                            }
                            url = `comments/quiz-attempt/${this.rootAttemptId}?user_id=${userId}`
                        }
                        if (this.questionId) {
                            url += `&quiz_question_id=${this.questionId}`
                        }
                    }
                }

                if (this.commentable_type === "info_units" && this.unitId) {
                    url = `comments/info_units/${this.unitId}`
                }

                if (this.parent === "student" && !isAllComments) {
                    url = `/public/${url}`
                }

                if (this.direction === "column-reverse") {
                    params.sort_type = "asc"
                }

                const response = await axios.get(url, { params })

                if (this.parent === "student") {
                    const payload = response.data?.comments || response.data
                    const { data, meta, is_user_comment_allowed } = payload

                    this.isUserCommentAllowed = is_user_comment_allowed
                    this.meta = meta
                    this.comments = data
                } else {
                    const { comments, unread_ids, commentable } = response.data
                    const { data, meta } = comments
                    this.comments = data
                    this.meta = meta
                    this.commentable = commentable
                    this.unreadIds = unread_ids
                }
            } catch (e) {
                console.log(e)
            } finally {
                this.shouldIgnoreCommentId = false
                if (!preventLoading) {
                    setTimeout(() => {
                        this.loadingGetComment = false
                    }, 300)
                }
            }
        },
        getLangs() {
            let timerInterval = null
            timerInterval = setInterval(() => {
                if (this.$parent && this.$parent.langsKeys) {
                    clearInterval(timerInterval)
                    this.langsKeys = this.$parent.langsKeys
                }
            }, 1000)
        },
        async getCurators() {
            if (this.courseId) {
                try {
                    let url = `/courses/${this.courseId}/user-curators`
                    if (this.quizId) {
                        url += `?quiz=${this.quizId}`
                    }
                    if (this.lessonId) {
                        if (this.quizId) {
                            url += "&"
                        } else {
                            url += "?"
                        }
                        url += `lesson_id=${this.lessonId}`
                    }
                    const res = await axios.get(url)
                    this.curators = res.data.data
                    this.curatorPublicFace = this.curators.find(curator => curator.is_public_user)
                    this.curatorsUpdateCount++
                } catch (e) {
                    console.log("get curators error:", e)
                }
            }
        }
    },
    computed: {
        computedAllowPublicAnswer() {
            // For quiz-attempt expert check if there are any public comments
            if (this.commentable_type === "quiz-attempt" && this.parent === "expert") {
                const hasPublicComments = this.comments.some(
                    comment => !comment.parent_id && comment.is_private === false
                )
                return this.allowPublicAnswer && hasPublicComments
            }
            return this.allowPublicAnswer
        },
        transitionName() {
            if (this.loadingGetComment) {
                return ""
            }
            return "move-right"
        },
        displayedComments() {
            return this.comments
        },
        createStyle() {
            const shadow = {
                boxShadow: "0px 4px 10px rgba(128, 158, 191, 0.15)",
                borderRadius: "10px",
                padding: "15px 5px 30px 5px",
                marginBottom: "10px"
            }
            const style = this.stickyBottom
                ? {
                      position: "sticky",
                      zIndex: 2,
                      bottom: 0,
                      backgroundColor: "#ffffff",
                      marginTop: "10px",
                      ...shadow
                  }
                : {
                      ...shadow
                  }

            if (this.noShadow) {
                delete style.boxShadow
                delete style.padding
                delete style.marginBottom
            }

            return style
        }
    },
    watch: {
        displayBulkDeleteModal(val) {
            EventBus.$emit(EventsList.BLUR_FULL_CONTENT, val)
        }
    }
}
</script>
<style lang="scss" scoped>
.course-comments-content {
    &::v-deep {
        .move-right-move {
            transition: all 0.2s;
        }

        .move-right-leave-active,
        .move-right-enter-active {
            transition: 0.2s ease-out;
        }

        .move-right-enter {
            transform-origin: top left;
            transform: translate(-20px, -20px) scale(1.04);
        }

        .move-right-leave-to {
            transform: translate(0px, 0) scale(1);
        }
    }
}
</style>
